import React from 'react';

// Decorative swoop background for the use-case hero band (see
// UseCaseView's `heroBanner` option) -- two overlapping ribbon shapes in a
// blue -> violet -> magenta/pink gradient over a lightly grained ground,
// approved design from the background-options mockups. Purely decorative
// (aria-hidden), absolutely positioned to fill its parent -- the parent is
// expected to be `position: relative` with real content layered on top.
//
// Was previously sized via aspect-[20/7] (the viewBox's own ratio) and
// bottom-anchored with preserveAspectRatio="meet" -- deliberately NOT
// h-full, so the graphic's rendered height followed only the container's
// WIDTH. That worked on desktop, where the hero box's width:height ratio
// stays roughly in the graphic's own range. It broke badly on mobile:
// the hero box goes single-column there (title, description, and buttons
// all stack), making the box much TALLER relative to its width than on
// desktop, while the width-driven graphic got proportionally shorter --
// so it shrank to a thin sliver pinned at the very bottom, leaving a tall
// blank gray gap above it with the title sitting on plain background
// instead of the intended swoosh/haze.
//
// Fixed by switching to a true "cover" fill: h-full + w-full (both driven
// by the parent, not just width) with preserveAspectRatio="xMidYMax
// slice". slice crops instead of letterboxing, so the graphic always
// fully fills the box on any aspect ratio -- no more blank gap above it,
// on any screen size. xMidYMax keeps the crop anchored to the bottom edge
// (the ribbons are deliberately drawn bleeding past the viewBox's own
// top/bottom, see the path coordinates below going negative and past
// 420), preserving the original clean-cut-at-the-bottom look this was
// designed around.
//
// That fixed the blank-gap problem but left a second, more subtle one on
// mobile: "slice" crops to COVER the box, and on a narrow-tall box that
// means zooming in until only a fairly narrow vertical strip of the
// 1200-wide viewBox is actually visible. The ribbons live around
// x=625-1450 (right half of the canvas, bleeding past its right edge);
// centered ("xMid", anchored on x=600) that narrow mobile strip landed
// almost entirely on the flat gray/haze LEFT of the ribbons, showing only
// a small triangle of color in the bottom-right corner -- easy to mistake
// for "the swoosh is barely there" when it's really "the crop window
// isn't over the swoosh at all". `align` controls the x-anchor of that
// crop (still always YMax so the bottom-cut look is preserved): "xMid"
// (the default, used on wider/shorter boxes like this one on desktop)
// centers it, "xMax" anchors it to the viewBox's right edge instead,
// which keeps a narrow crop inside the ribbons' own x-range so it stays
// colorful instead of mostly gray.
//
// `mobileBlurPx` softens that same mobile crop with a CSS blur -- added
// after switching mobile to "xMax": the crop lands inside the ribbons, but
// text laid straight over their sharp color transitions (navy to violet to
// pink) didn't have reliable contrast everywhere, and a translucent card
// behind the text just to fix that read as a jarring hard-edged rectangle
// floating on top of an otherwise sharp graphic. Blurring the graphic
// itself removes the sharp transitions so text can sit directly on it
// (paired with UseCaseView's dark scrim + white text, not a boxed card).
// Implemented as `filter: blur(...)` plus a matching CSS `scale(...)` on
// the same element: blur softly fades an element's edges toward
// transparent, which right at this element's own box boundary would show
// as a faint ring where the parent's `overflow-hidden` cuts it off --
// scaling the (already-absolutely-positioned, cover-filled) svg up first
// pushes that fade outside the visible box before the blur ever gets
// clipped, so what's left inside is uniformly blurred with no edge
// artifact. Scale factor is picked from the blur radius (roughly +2.5%
// per blurred px, floored at 1) rather than a fixed number, so a bigger
// blur -- which fades further in -- doesn't outrun a small fixed scale.
//
// `mobileAlign` lets a caller give a DIFFERENT align just for narrow
// viewports (Tailwind's `sm` breakpoint, 640px) than the one used at `sm`
// and up, since the same box can be wide-and-short on desktop (where
// "xMid" already shows both ribbons fine) and narrow-and-tall on mobile
// (where it needs "xMax" to stay on the colorful side) -- exactly
// UseCaseView's hero band, which stacks to a single column on mobile.
// Left unset, it just falls back to `align`, so a caller whose box is
// narrow-tall at every width (nothing currently is, but was anticipated
// when `align` was first added) can still pass one fixed `align` and get
// the same crop everywhere. When `mobileAlign` differs from `align`, this
// renders two copies of the graphic (each with its own uniquely-suffixed
// gradient/filter ids, so they can't collide) and lets Tailwind's
// `sm:hidden` / `hidden sm:block` pick the right one per breakpoint --
// simpler and more reliable than trying to swap an SVG's
// preserveAspectRatio attribute at a media-query breakpoint in CSS, which
// isn't something CSS can actually do.
const HeroSwoopBackground = ({ align = 'xMid', mobileAlign, mobileBlurPx = 0 }) => {
  const desktopAlign = align;
  const smallAlign = mobileAlign || align;
  const isResponsive = smallAlign !== desktopAlign;
  // See the mobileBlurPx comment above for why scale rides along with blur.
  const mobileScale = 1 + Math.max(0, mobileBlurPx) * 0.025;

  const renderSwoop = (idSuffix, preserveAlign, extraClassName, blurPx = 0, scale = 1) => (
    <svg
      key={idSuffix}
      className={`absolute inset-0 h-full w-full ${extraClassName}`}
      style={blurPx > 0 ? { filter: `blur(${blurPx}px)`, transform: `scale(${scale})` } : undefined}
      viewBox="0 0 1200 420"
      preserveAspectRatio={`${preserveAlign}YMax slice`}
      aria-hidden="true"
    >
      <defs>
        <filter id={`heroSwoopGrain-${idSuffix}`}>
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" result="noise" />
          <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.035 0" />
        </filter>
        <filter id={`heroSwoopHazeBlur-${idSuffix}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="70" />
        </filter>
        <linearGradient id={`heroSwoopRibbon1-${idSuffix}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#010f45" />
          <stop offset="35%" stopColor="#012497" />
          <stop offset="65%" stopColor="#6d28d9" />
          <stop offset="100%" stopColor="#a21caf" />
        </linearGradient>
        <linearGradient id={`heroSwoopRibbon2-${idSuffix}`} x1="10%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1638c2" />
          <stop offset="40%" stopColor="#9333ea" />
          <stop offset="75%" stopColor="#e879f9" />
          <stop offset="100%" stopColor="#fb7185" />
        </linearGradient>
      </defs>

      <rect width="1200" height="420" fill="#e7e6e1" />
      <rect width="1200" height="420" filter={`url(#heroSwoopGrain-${idSuffix})`} />

      {/* Soft purple/pink haze glow behind the ribbons, for atmosphere --
          heavily blurred so it reads as ambient light rather than shapes.
          Concentrated bottom-left/center; top-left is left mostly bare gray,
          and the right-side glow is kept faint. */}
      <g filter={`url(#heroSwoopHazeBlur-${idSuffix})`}>
        <ellipse cx="980" cy="60" rx="240" ry="170" fill="#e9b3ee" opacity="0.32" />
        <ellipse cx="760" cy="360" rx="240" ry="170" fill="#fbcfe8" opacity="0.4" />
        <ellipse cx="1120" cy="300" rx="180" ry="140" fill="#d8b4fe" opacity="0.2" />
        <ellipse cx="260" cy="420" rx="280" ry="190" fill="#f5d0fe" opacity="0.4" />
        <ellipse cx="120" cy="330" rx="220" ry="170" fill="#e9b3ee" opacity="0.34" />
      </g>

      {/* Ribbon 1: reshaped in Figma (see swoop-curve-editor / Figma export
          flow) -- coordinates translated back from Figma's own tight
          bounding-box export into this component's 1200x420 canvas. */}
      <path
        d="M 1400 -100 C 1080 -60, 1000 100, 1080 200 C 1117.29 246.61, 1129.11 330.302, 1047 367 C 952.949 409.036, 753.136 403.288, 625 446 L 680 503 C 812.953 463.606, 1039.46 487.56, 1143 434 C 1249.73 378.791, 1230.76 245.834, 1180 190 C 1090 100, 1150 10, 1450 -60 Z"
        fill={`url(#heroSwoopRibbon1-${idSuffix})`}
        opacity="0.95"
      />

      {/* Ribbon 2: same Figma-reshaped edit, trails behind ribbon 1. */}
      <path
        d="M 1300 -100 C 1010 -40, 930 130, 1010 220 C 1033.96 246.958, 1071.75 354.549, 983 376 C 915.103 392.412, 876.776 339.328, 819 355 C 776 366.664, 760 398, 693 443 L 730 488 C 780.059 475.022, 796 425, 827 425 C 915.292 424.069, 941.655 472.504, 990 456 C 1134.12 406.802, 1128.8 260.898, 1080 206 C 1000 116, 1050 30, 1330 -60 Z"
        fill={`url(#heroSwoopRibbon2-${idSuffix})`}
        opacity="0.85"
      />
    </svg>
  );

  if (!isResponsive) {
    return renderSwoop('only', desktopAlign, '');
  }

  return (
    <>
      {renderSwoop('mobile', smallAlign, 'sm:hidden', mobileBlurPx, mobileScale)}
      {renderSwoop('desktop', desktopAlign, 'hidden sm:block')}
    </>
  );
};

export default HeroSwoopBackground;
